import {useReachGoalCallback, withSuspenseLoader} from '@logistics-frontend/blocks'
import {DrawerOverlay, DrawerSection} from '@logistics-frontend/blocks/legacy'
import clsx from 'clsx'
import React, {lazy, useCallback, useContext, useMemo} from 'react'

import {SharedRouteInfoResponse} from 'src/api/cargo-c2c/types'
import {isMarketIntegration} from 'src/config'
import {isBannerPositionUp} from 'src/utils'

import {SharedRoutePageContext} from '../../context'
import {useIsMobile, useSharedRoutePerformer} from '../../hooks'
import {Brand} from '../../types'
import {getMapProps} from '../../utils'
import {Content} from '../content'
import {DrawerDesktop, DrawerMobile, DrawerSection as DrawerSectionDesktop} from '../drawer'
import {Feedback} from '../feedback'
import {Header} from '../header'
import {Map} from '../map'
import {Payment} from '../payment'
import {Poll} from '../poll'
import {PostcardDesktop, PostcardMobile} from '../postcard'
import {DesktopPromoContainer, MobilePromoContainer, PromoProps, YandexPromo, YangoPromo} from '../promo'
import {PromoBanner as ExperimentalPromoBanner} from '../promo-banner'
import {METRIKA_GOALS} from '../promo-banner/PromoBanner.consts'
import {Status} from '../status'
import {Timeline} from '../timeline'
import {classes} from './ActiveScreen.classes'
import {isCountry, toActionsPayload} from './ActiveScreen.utils'

const Actions = withSuspenseLoader(lazy(() => import('../actions')))
const AutoBanner = withSuspenseLoader(lazy(() => import('../auto-banner')))
const WebTips = withSuspenseLoader(lazy(() => import('../web-tips')))

type Props = {
  info: SharedRouteInfoResponse
}

const isHideBanners = isMarketIntegration()

const ActiveScreen: React.FC<Props> = ({info}) => {
  const {brand} = useContext(SharedRoutePageContext)
  const Promo: React.FC<PromoProps> = brand === Brand.Yango ? YangoPromo : YandexPromo
  const performer = useSharedRoutePerformer()
  const {mapProps, headActions, paymentAction, webTipsAction, footActions, isShowFeedback} = useMemo(() => {
    return Object.assign(toActionsPayload(info), {
      mapProps: getMapProps(info, performer),
    })
  }, [info, performer])
  const isMobile = useIsMobile()
  const isNotTurkey = !isCountry('tr')

  const reachGoalCallback = useReachGoalCallback()

  const onOldBannerClick = useCallback(() => {
    // @ts-expect-error CARGODEV-20330
    reachGoalCallback(METRIKA_GOALS.OLD_INSTALL_BANNER_CLICK)
  }, [reachGoalCallback])

  // вероятность того что в info изменится content_sections мала,
  // а если и изменится, то toString сработает и обновит,
  // но зато мы избегаем лишних перерисовок при поллинга, из-за которого еще доп запросы были к иконкам
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const contentSections = useMemo(() => info.content_sections, [info.content_sections?.toString()])

  const {DrawerComponent, DrawerSectionComponent, Postcard} = useMemo(() => {
    if (isMobile) {
      return {
        DrawerComponent: DrawerMobile,
        DrawerSectionComponent: DrawerSection,
        Postcard: PostcardMobile,
      }
    }

    return {
      DrawerComponent: DrawerDesktop,
      DrawerSectionComponent: DrawerSectionDesktop,
      Postcard: PostcardDesktop,
    }
  }, [isMobile])

  const isMobileBannerShown = isMobile && !info.postcard
  const isExperimentalPromoBannerShown = isBannerPositionUp(info)
  const deeplink = info.promo_banner?.trail_button?.deeplink || info.promo_banner?.lead_button?.deeplink

  return (
    /**
     * Пора переходить на новый дровер - CARGODEV-34815
     */
    <DrawerOverlay zIndex={1000}>
      {/* Карте становится плохо при RTL, маршрут съезжает */}
      <div dir="ltr">
        <Map {...mapProps} />
      </div>
      <Header variant={info.postcard ? 'postcard' : undefined} />
      <DrawerComponent>
        {isMobile && <AutoBanner />}
        {!!info.poll && (
          <div className={classes.drawerSection}>
            <DrawerSectionComponent>
              <Poll poll={info.poll} />
            </DrawerSectionComponent>
          </div>
        )}
        <div className={clsx(classes.drawerSection, classes.content)}>
          <DrawerSectionComponent>
            <Status info={info} />
            {info.timeline && <Timeline timeline={info.timeline} />}
            {isShowFeedback && <Feedback />}
            {webTipsAction && <WebTips webTipsAction={webTipsAction} />}
            {headActions.length > 0 && <Actions actions={headActions} />}
          </DrawerSectionComponent>
          {paymentAction && (
            <DrawerSectionComponent>
              <Payment paymentAction={paymentAction} />
            </DrawerSectionComponent>
          )}
          {isMobileBannerShown && !isExperimentalPromoBannerShown && !isHideBanners && (
            <DrawerSectionComponent>
              {isNotTurkey ? (
                <div className={classes.promoWrapper}>
                  <Promo deeplink={deeplink} onClick={onOldBannerClick} fullWidth/>
                </div>
              ) : null}
            </DrawerSectionComponent>
          )}

          {contentSections && <Content sections={contentSections} />}
          {footActions.length > 0 && (
            <DrawerSectionComponent fullHeight>
              <Actions actions={footActions} />
            </DrawerSectionComponent>
          )}
        </div>
      </DrawerComponent>

      {info.postcard && <Postcard data={info.postcard} />}

      {/**
       * TODO: Подумать. Сейчас не понятно, почему контейнер под промо лежит рядом с промо.
       * Отвечает за положение на этом экране. Сейчас сделал по аналогии с тем, что есть
       */}
      {isMobileBannerShown && isExperimentalPromoBannerShown && !isHideBanners && (
        <MobilePromoContainer>
          <ExperimentalPromoBanner {...info.promo_banner} />
        </MobilePromoContainer>
      )}

      {!isMobileBannerShown && isNotTurkey && !isHideBanners ? (
        <DesktopPromoContainer>
          <Promo deeplink={deeplink} onClick={onOldBannerClick} />
          {!isMobile && <AutoBanner />}
        </DesktopPromoContainer>
      ) : null}
    </DrawerOverlay>
  )
}

export default ActiveScreen
